import type { RepeatableBlockStateType } from '@unifyapps/defs/blocks/Repeatable/types';
import _get from 'lodash/get';
import { isDynamicValue } from './dynamicBindings';
import { oldEvaluateExpression } from './evaluateExpression';
import { resolveHardenedPathExpression } from './recursiveUpdate';

/**
 * special index option constant - when user selects "Index" in repeatable primary key
 */
export const INDEX_OPTION_KEY = '__index__';

/**
 * unified function to get primary key value from data item
 * handles all formats: object with path, string expression, and index fallback
 *
 * @param dataItem - the data item from which to extract the primary key
 * @param index - the array index of the item (used as fallback)
 * @param primaryKey - the primary key configuration (object or string)
 * @returns the primary key value as a string
 */
export function getPrimaryKeyValue({
  dataItem,
  index,
  primaryKey,
}: {
  dataItem: unknown;
  index: number;
  primaryKey: RepeatableBlockStateType['content']['primaryKey'];
}): string {
  // no primary key configured - use index
  if (!primaryKey) {
    return String(index);
  }

  // object format: { path: string, key: string }
  if (typeof primaryKey === 'object') {
    // special case: user selected "Index" option
    if (primaryKey.key === INDEX_OPTION_KEY) {
      return String(index);
    }

    // extract value from data item using path
    const value = _get(dataItem, primaryKey.path);
    return value !== undefined && value !== null ? String(value) : String(index);
  }

  // When user provides a custom static string path, we can directly get the value from the data item
  // for static strings like "createdTime" or "properties.name"
  if (typeof primaryKey === 'string' && !isDynamicValue(primaryKey)) {
    const value = _get(dataItem, primaryKey);
    return value !== undefined && value !== null ? String(value) : String(index);
  }

  // old string format: expression like "{{ dataItem.id }}". This is a pure property path, so read
  // it by walking the object instead of compiling it — a value inside dataItem (repeatable data
  // can be bound to attacker-controlled pageInputs) then can't be re-evaluated as code on the
  // server (UA-001 class). Transparent: a pure-path read returns the same value the eval would.
  // Only a non-path custom expression (rare) still falls through to eval.
  const hardened = resolveHardenedPathExpression(primaryKey, { dataItem });
  if (hardened.handled) {
    return hardened.value != null ? String(hardened.value) : String(index);
  }

  const evaluatedValue = oldEvaluateExpression<string>({
    context: { dataItem },
    expression: primaryKey,
  });

  return evaluatedValue ?? String(index);
}

/**
 * converts primary key to expression format for evaluation
 * used when preparing primary key for data source computation
 *
 * @param primaryKey - the primary key configuration
 * @returns expression string or undefined
 */
export function primaryKeyToExpression(
  primaryKey: RepeatableBlockStateType['content']['primaryKey'],
): string | undefined {
  if (!primaryKey) {
    return undefined;
  }

  // object format: convert to expression
  if (typeof primaryKey === 'object') {
    // index option doesn't need expression conversion
    if (primaryKey.key === INDEX_OPTION_KEY) {
      return undefined;
    }
    return `{{ dataItem.${primaryKey.path} }}`;
  }

  // already a string expression
  return primaryKey;
}
